import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  cancelTrainingBooking,
  getMyTrainingBookings,
  getTrainingById,
  getTrainingInvitations,
  getTrainingList,
  getTrainingRounds,
  registerTrainingRound,
} from 'services/eventCalendarApi'
import {
  FilterTrainingInput,
  RegisterRoundInput,
  ResponseMyBookings,
  ResponseRegisterRound,
  ResponseTrainingDetail,
  ResponseTrainingInvitations,
  ResponseTrainingList,
  ResponseTrainingRounds,
  TrainingRound,
  TrainingRoundsSummary,
} from 'services/interface/eventCalendar/eventCalendar.interface'
import { formatDates } from 'utils/dateFormat'

/**
 * รอบถัดไปที่ยัง "ไม่ผ่านไปแล้ว" — backend ยังส่ง next_available_round ของวันที่ผ่านมาได้
 * (คืน null = ไม่มีวันที่ที่เชื่อถือได้จะโชว์ ไม่ได้แปลว่าหลักสูตรจบแล้ว)
 */
export const upcomingRound = (summary?: TrainingRoundsSummary): TrainingRound | null => {
  const round = summary?.next_available_round

  return round && round.round_date >= formatDates(new Date()) ? round : null
}

/**
 * หลักสูตรยังมีรอบข้างหน้าไหม — ดูที่ last_round_date (วันรอบสุดท้าย) เป็นหลัก
 * เพราะ next_available_round ของ list อาจค้างอยู่ที่รอบที่ผ่านไปแล้ว
 */
export const hasUpcomingRound = (summary?: TrainingRoundsSummary): boolean => {
  if (!summary) return false
  const lastDate = summary.last_round_date ?? summary.next_available_round?.round_date

  return !!lastDate && lastDate >= formatDates(new Date())
}

export const TRAINING_KEYS = {
  announcements: ['trainingAnnouncements'] as const,
  list: 'trainingList',
  detail: 'trainingDetail',
  rounds: 'trainingRounds',
  myBookings: ['trainingMyBookings'] as const,
  invitations: ['trainingInvitations'] as const,
}

/**
 * หลักสูตรที่ยังมีรอบข้างหน้า — ป้ายประกาศเชิญเข้าร่วมใช้ตัวนี้
 * ดึงแค่ 5 รายการ และ cache 5 นาที (ป้ายไม่จำเป็นต้องสดวินาทีต่อวินาที)
 */
export const useTrainingAnnouncements = (
  enabled = true
): UseQueryResult<ResponseTrainingList, Error> => {
  return useQuery<ResponseTrainingList, Error>({
    queryKey: TRAINING_KEYS.announcements,
    queryFn: () => getTrainingList({ has_upcoming_round: true, limit: 5, offset: 0 }),
    enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  })
}

/**
 * คำเชิญอบรมที่ยังรอตอบ
 *
 * ร้านที่ไม่ได้มาจากลีดได้ [] เสมอ — ไม่ retry และ cache ยาวหน่อย เพราะคำเชิญเปลี่ยน
 * ตอนทีมงานส่งเมล ไม่ใช่ทุกวินาที (จองแล้วเรา invalidate เองอยู่แล้ว)
 */
export const useTrainingInvitations = (): UseQueryResult<ResponseTrainingInvitations, Error> => {
  return useQuery<ResponseTrainingInvitations, Error>({
    queryKey: TRAINING_KEYS.invitations,
    queryFn: () => getTrainingInvitations(),
    staleTime: 1000 * 60,
    retry: false,
  })
}

export const useTrainingList = (
  input: FilterTrainingInput
): UseQueryResult<ResponseTrainingList, Error> => {
  return useQuery<ResponseTrainingList, Error>({
    queryKey: [TRAINING_KEYS.list, input.search, input.has_upcoming_round, input.offset, input.limit],
    queryFn: () => getTrainingList(input),
  })
}

export const useTrainingDetail = (
  eventId?: string
): UseQueryResult<ResponseTrainingDetail, Error> => {
  return useQuery<ResponseTrainingDetail, Error>({
    queryKey: [TRAINING_KEYS.detail, eventId],
    queryFn: () => getTrainingById(eventId as string),
    enabled: !!eventId,
    // จำนวนที่นั่งเหลือเปลี่ยนได้ทุกเมื่อ — ต้องสดตอนเปิดดูเพื่อจอง
    staleTime: 1000 * 15,
  })
}

export const useTrainingRounds = (
  eventId?: string
): UseQueryResult<ResponseTrainingRounds, Error> => {
  return useQuery<ResponseTrainingRounds, Error>({
    queryKey: [TRAINING_KEYS.rounds, eventId],
    queryFn: () => getTrainingRounds(eventId as string),
    enabled: !!eventId,
    staleTime: 1000 * 15,
  })
}

export const useMyTrainingBookings = (input?: {
  upcoming_only?: boolean
}): UseQueryResult<ResponseMyBookings, Error> => {
  return useQuery<ResponseMyBookings, Error>({
    queryKey: [...TRAINING_KEYS.myBookings, input?.upcoming_only],
    queryFn: () => getMyTrainingBookings(input),
  })
}

/**
 * จองรอบ — invalidate ทั้ง 4 คีย์ เพราะ
 * ป้ายประกาศ / รายการ / รายละเอียด / รอบที่ฉันจอง เปลี่ยนพร้อมกันหมด
 *
 * ไม่ใส่ onError ที่นี่ ปล่อยให้ผู้เรียกจัดการ (ต้องแยกเคส EMAIL_REQUIRED
 * ออกจาก error ทั่วไปเพื่อเปิด modal ให้ไปใส่อีเมล)
 */
export const useRegisterTrainingRound = (): UseMutationResult<
  ResponseRegisterRound,
  Error,
  RegisterRoundInput
> => {
  const queryClient = useQueryClient()

  return useMutation<ResponseRegisterRound, Error, RegisterRoundInput>({
    mutationFn: input => registerTrainingRound(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRAINING_KEYS.announcements })
      queryClient.invalidateQueries({ queryKey: [TRAINING_KEYS.list] })
      queryClient.invalidateQueries({ queryKey: [TRAINING_KEYS.detail] })
      queryClient.invalidateQueries({ queryKey: [TRAINING_KEYS.rounds] })
      queryClient.invalidateQueries({ queryKey: TRAINING_KEYS.myBookings })
      // จองแล้ว = ตอบคำเชิญของหลักสูตรนั้นแล้ว การ์ดต้องหายไปเอง (backend คำนวณจากการจองจริง)
      queryClient.invalidateQueries({ queryKey: TRAINING_KEYS.invitations })
    },
  })
}

export const useCancelTrainingBooking = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: attendeeId => cancelTrainingBooking(attendeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRAINING_KEYS.announcements })
      queryClient.invalidateQueries({ queryKey: [TRAINING_KEYS.list] })
      queryClient.invalidateQueries({ queryKey: [TRAINING_KEYS.detail] })
      queryClient.invalidateQueries({ queryKey: [TRAINING_KEYS.rounds] })
      queryClient.invalidateQueries({ queryKey: TRAINING_KEYS.myBookings })
      // จองแล้ว = ตอบคำเชิญของหลักสูตรนั้นแล้ว การ์ดต้องหายไปเอง (backend คำนวณจากการจองจริง)
      queryClient.invalidateQueries({ queryKey: TRAINING_KEYS.invitations })
    },
  })
}
